import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { LayoutTemplate } from '@ghostscreen/contracts';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { apiErrorMessage } from '../i18n';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { LayoutDrawer } from './layouts/LayoutDrawer';
import { LayoutsListCard } from './layouts/LayoutsListCard';
import { TemplatePickerCard } from './layouts/TemplatePickerCard';

/**
 * Layouts — start from a shape, assign playlists per zone (Layouts.dc.html).
 */
export function LayoutsPage() {
  const { t } = useTranslation('layouts');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const creatingFromHash = useRef(false);

  const layoutsQuery = useQuery({
    queryKey: ['layouts', organisationId],
    queryFn: () => api.listLayouts(organisationId),
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });

  const retry = useQueryRetry(async () => {
    await Promise.all([layoutsQuery.refetch(), playlistsQuery.refetch()]);
  });

  const layouts = layoutsQuery.data ?? [];
  const playlists = playlistsQuery.data ?? [];
  const selected = useMemo(
    () => layouts.find((layout) => layout.id === selectedId) ?? null,
    [layouts, selectedId],
  );

  const playlistName = useMemo(() => {
    const map = new Map(playlists.map((playlist) => [playlist.id, playlist.name]));
    return (playlistId: string | null) => (playlistId ? map.get(playlistId) ?? null : null);
  }, [playlists]);

  const create = useMutation({
    mutationFn: (template: LayoutTemplate) => api.createLayout(organisationId, { template }),
    onSuccess: async (layout) => {
      creatingFromHash.current = false;
      setCreateError(null);
      await queryClient.invalidateQueries({ queryKey: ['layouts', organisationId] });
      setSelectedId(layout.id);
    },
    onError: (error) => {
      creatingFromHash.current = false;
      setCreateError(apiErrorMessage(error, t('page.createError')));
    },
  });

  useEffect(() => {
    if (location.hash !== '#new' || creatingFromHash.current) return;
    creatingFromHash.current = true;
    navigate({ pathname: '/layouts', search: location.search }, { replace: true });
    create.mutate('full');
    // Design CTA "New layout" creates a Full screen layout, then opens the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hash-driven create only
  }, [location.hash]);

  const loading = layoutsQuery.isPending || playlistsQuery.isPending;
  const failed = layoutsQuery.isError || playlistsQuery.isError;

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-5 self-stretch"
      style={{
        animation: 'gs-in 260ms ease both',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {failed || retry.isRetrying ? (
        <QueryErrorState
          message={t('page.loadError')}
          onRetry={retry.retry}
          isRetrying={retry.isRetrying}
        />
      ) : loading ? (
        <QueryLoadingBlock />
      ) : (
        <>
          <TemplatePickerCard
            busy={create.isPending}
            onPick={(template) => create.mutate(template)}
          />
          <LayoutsListCard
            layouts={layouts}
            playlistName={playlistName}
            onOpen={setSelectedId}
          />
          {createError ? (
            <p className="m-0 text-sm font-semibold" style={{ color: 'var(--gs-bad)' }}>
              {createError}
            </p>
          ) : null}
        </>
      )}

      <LayoutDrawer
        open={Boolean(selected)}
        organisationId={organisationId}
        layout={selected}
        playlists={playlists}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
